import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {startOfUtcHour} from '@shared/lib/utils/date_utils';
import {asNumber} from '@shared/lib/utils/type_utils';
import {
  ALL_DOOR_COUNTER_NAMES,
  DoorCounterName,
  DoorCountersDataPoint,
} from '@shared/models/api_models';

import {
  MEDIA_QUERY_DESKTOP_ONLY,
  MEDIA_QUERY_MOBILE_ONLY,
  MEDIA_QUERY_TABLET_ONLY,
} from '@shared_web/breakpoints';
import {Flex} from '@shared_web/components/core2/flex';
import {notifyError} from '@shared_web/lib/notification';

import {BarChart, CHART_HEIGHT} from '@mavismart_frontend/components/bar_chart';
import {Loader} from '@mavismart_frontend/components/loader';
import {maybeTranslate, Translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {
  SELECT_THEME,
  SELECT_WRAPPER_THEME,
  TEXT_MAIN_COLOR,
} from '@mavismart_frontend/lib/constants';
import {DoorChartForm, DoorChartFormData} from '@mavismart_frontend/pages/tabs/door_chart_form';
import {DoorStateView} from '@mavismart_frontend/pages/tabs/door_state_view';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

interface DoorStatisticsProps {
  doorId: string;
}

export const DoorStatistics: FC<DoorStatisticsProps> = ({doorId}) => {
  const account = useAccount();
  const supportAppEnabled = useMemo(
    () => account?.hasSupportApp === true && account?.supportAppEnabled === true,
    [account]
  );
  const lang = getUserLanguage(account);

  const [counters, setCounters] = useState<DoorCounterName[]>(['door_cycles', 'door_time_open']);
  const [chartData, setChartData] = useState<DoorCountersDataPoint[]>();
  const [chartFormData, setChartFormData] = useState<DoorChartFormData | undefined>();
  const [resetInitialData, setResetInitialData] = useState<DoorChartFormData | undefined>();

  // Fetch the data when the form data or the counters changes
  useEffect(() => {
    if (!chartFormData) {
      return;
    }
    setChartData(undefined);
    const {breakdown, startTs, endTs} = chartFormData;
    apiCall('POST /get-door-counters', {doorId, breakdown, counters, range: {startTs, endTs}})
      .then(setChartData)
      .catch(notifyError);
  }, [doorId, chartFormData, counters]);

  // Prepare the data for the charts
  const countersAndData = useMemo(
    () =>
      counters.map(counter => {
        const data = chartData?.map(entry => ({ts: entry.ts, value: entry[counter] ?? 0}));
        const total = data?.reduce((sum, point) => sum + point.value, 0) ?? 0;
        const otherCounters = counters.filter(c => c !== counter);
        const countersDisplayed = ALL_DOOR_COUNTER_NAMES.filter(
          c => !otherCounters.includes(c as DoorCounterName)
        ).map(counter => ({value: counter, label: maybeTranslate(counter, lang)}));
        return {counter, data, countersDisplayed, total};
      }),
    [chartData, counters, lang]
  );

  const handleCounterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = asNumber(e.target.dataset['index']);
    const val = e.target.value as DoorCounterName;
    if (index === undefined) {
      return;
    }
    setCounters(current => current.map((c, i) => (i === index ? val : c)));
  }, []);

  const handleBarClick = useCallback(
    (_index: number, data: {ts: number; value: number}) => {
      const {breakdown} = chartFormData ?? {};
      if (breakdown === 'day') {
        setResetInitialData({
          breakdown: 'hour',
          startTs: startOfUtcHour(new Date(data.ts)).getTime(),
          endTs: startOfUtcHour(new Date(data.ts), 23).getTime(), // 23 istead of 24 because endTs is inclusive
        });
      }
    },
    [chartFormData]
  );

  return (
    <Wrapper $column $gap="xxl" $width="100%" $align="center">
      <DoorChartForm initialData={resetInitialData} onChange={setChartFormData} />
      {chartFormData &&
        countersAndData.map(({counter, data, countersDisplayed, total}, index) => (
          <Flex $column $align="center" $width="100%" $gap="s" key={counter}>
            {supportAppEnabled ? (
              <SelectWrapper>
                <StyledSelect value={counter} data-index={index} onChange={handleCounterChange}>
                  {countersDisplayed.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </StyledSelect>
              </SelectWrapper>
            ) : (
              <CharTitle>{maybeTranslate(counter, lang)}</CharTitle>
            )}
            {data ? (
              <>
                <SummaryCard>
                  <SummaryLabel>
                    <Translate value="total" />
                  </SummaryLabel>
                  <SummaryValue>{total.toLocaleString()}</SummaryValue>
                </SummaryCard>
                <StyledBarChart
                  data={data}
                  breakdown={chartFormData.breakdown}
                  onBarClick={handleBarClick}
                />
              </>
            ) : (
              <ChartLoaderWrapper>
                <Loader />
              </ChartLoaderWrapper>
            )}
          </Flex>
        ))}
      {supportAppEnabled && <StyledDoorStateView doorId={doorId} />}
    </Wrapper>
  );
};

DoorStatistics.displayName = 'DoorStatistics';

const Wrapper = styled(Flex)`
`;

const CharTitle = styled.div`
  font-size: 16px;
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 8px 0;
  width: 100%;
  max-width: 700px;
  box-sizing: border-box;
`;

const SummaryLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${TEXT_MAIN_COLOR};
`;

const SummaryValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${TEXT_MAIN_COLOR};
`;

const StyledBarChart = styled(BarChart)`
  margin-right: -8px;
  width: 100%;
  ${MEDIA_QUERY_MOBILE_ONLY} {
    max-width: 500px;
  }
  ${MEDIA_QUERY_TABLET_ONLY} {
    max-width: 700px;
  }
  ${MEDIA_QUERY_DESKTOP_ONLY} {
    max-width: 700px;
  }
`;

const StyledSelect = styled.select`
  width: 260px;
  overflow: hidden;
  padding: 10px 40px 10px 12px;
  font-size: 14px;
  ${SELECT_THEME}
`;

const SelectWrapper = styled.div`
  ${SELECT_WRAPPER_THEME}
`;

const ChartLoaderWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${CHART_HEIGHT}px;
`;

const StyledDoorStateView = styled(DoorStateView)`
  ${MEDIA_QUERY_MOBILE_ONLY} {
    max-width: 500px;
  }
  ${MEDIA_QUERY_TABLET_ONLY} {
    max-width: 700px;
  }
  ${MEDIA_QUERY_DESKTOP_ONLY} {
    max-width: 700px;
  }
  margin-bottom: 16px;
`;
